import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Palette, Upload, Trash2, Loader2, AlertTriangle, Type } from "lucide-react";
import { useTenant } from "@/hooks/useTenant";
import { useState, useEffect, useRef, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { contrastRatio, generateChartPalette, hexToHSL } from "@/lib/colorUtils";

const DEFAULT_PRIMARY = '#186DE9';
const DEFAULT_ACCENT = '#2C9C7F';
const WCAG_AA_RATIO = 4.5;
const MAX_LOGO_SIZE = 2 * 1024 * 1024; // 2MB
const ACCEPTED_TYPES = ['image/png', 'image/svg+xml', 'image/jpeg', 'image/jpg'];

const FONT_OPTIONS = [
  { value: '__default__', label: 'Inter (Default)', style: 'Inter' },
  { value: 'Plus Jakarta Sans', label: 'Plus Jakarta Sans', style: 'Plus Jakarta Sans' },
  { value: 'DM Sans', label: 'DM Sans', style: 'DM Sans' },
  { value: 'Source Sans Pro', label: 'Source Sans Pro', style: 'Source Sans Pro' },
  { value: 'Lato', label: 'Lato', style: 'Lato' },
  { value: 'Nunito Sans', label: 'Nunito Sans', style: 'Nunito Sans' },
  { value: 'Outfit', label: 'Outfit', style: 'Outfit' },
  { value: 'Poppins', label: 'Poppins', style: 'Poppins' },
] as const;

export default function BrandingSettings() {
  const { currentOrg, isLoading: tenantLoading } = useTenant();
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Form state
  const [primaryColor, setPrimaryColor] = useState(DEFAULT_PRIMARY);
  const [accentColor, setAccentColor] = useState(DEFAULT_ACCENT);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [fontFamily, setFontFamily] = useState<string>('__default__');
  const [customFont, setCustomFont] = useState('');

  // Load current org data
  useEffect(() => {
    if (currentOrg) {
      setPrimaryColor(currentOrg.primary_color || DEFAULT_PRIMARY);
      setAccentColor(currentOrg.accent_color || DEFAULT_ACCENT);
      setLogoUrl(currentOrg.logo_url || null);
      const orgFont = currentOrg.font_family || '__default__';
      const isPreset = FONT_OPTIONS.some(f => f.value === orgFont);
      if (isPreset || orgFont === '__default__') {
        setFontFamily(orgFont);
      } else {
        setFontFamily('__custom__');
        setCustomFont(orgFont);
      }
    }
  }, [currentOrg]);

  // Contrast warnings
  const primaryContrast = useMemo(() => contrastRatio(primaryColor, '#FFFFFF'), [primaryColor]);
  const accentContrast = useMemo(() => contrastRatio(accentColor, '#FFFFFF'), [accentColor]);
  const showPrimaryWarning = primaryContrast < WCAG_AA_RATIO;
  const showAccentWarning = accentContrast < WCAG_AA_RATIO;

  // Chart palette preview
  const chartPalette = useMemo(
    () => generateChartPalette(primaryColor, accentColor),
    [primaryColor, accentColor]
  );

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !currentOrg?.id) return;

    if (!ACCEPTED_TYPES.includes(file.type)) {
      toast.error('Invalid file type', { description: 'Please upload PNG, SVG, or JPG.' });
      return;
    }
    if (file.size > MAX_LOGO_SIZE) {
      toast.error('File too large', { description: 'Logo must be under 2MB.' });
      return;
    }

    setUploading(true);
    try {
      const ext = file.name.split('.').pop() || 'png';
      const path = `${currentOrg.id}/logo.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('org-assets')
        .upload(path, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('org-assets')
        .getPublicUrl(path);

      // Save to org record
      const { error: updateError } = await supabase
        .from('orgs')
        .update({ logo_url: publicUrl })
        .eq('id', currentOrg.id);

      if (updateError) throw updateError;

      setLogoUrl(publicUrl);
      toast.success('Logo uploaded');
    } catch (err) {
      toast.error('Upload failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveLogo = async () => {
    if (!currentOrg?.id) return;
    setSaving(true);
    try {
      // Remove from storage (best effort — file may not exist)
      await supabase.storage.from('org-assets').remove([`${currentOrg.id}/logo.png`, `${currentOrg.id}/logo.svg`, `${currentOrg.id}/logo.jpg`]);

      const { error } = await supabase
        .from('orgs')
        .update({ logo_url: null })
        .eq('id', currentOrg.id);

      if (error) throw error;

      setLogoUrl(null);
      toast.success('Logo removed');
    } catch (err) {
      toast.error('Failed to remove logo', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveColors = async () => {
    if (!currentOrg?.id) {
      toast.error('No organization found');
      return;
    }
    setSaving(true);
    try {
      const resolvedFont = fontFamily === '__custom__' ? (customFont.trim() || null) : (fontFamily === '__default__' ? null : fontFamily);
      const { error } = await supabase
        .from('orgs')
        .update({
          primary_color: primaryColor === DEFAULT_PRIMARY ? null : primaryColor,
          accent_color: accentColor === DEFAULT_ACCENT ? null : accentColor,
          font_family: resolvedFont,
        })
        .eq('id', currentOrg.id);

      if (error) throw error;
      toast.success('Branding saved');
      window.location.reload();
    } catch (err) {
      toast.error('Failed to save', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleResetDefaults = async () => {
    if (!currentOrg?.id) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from('orgs')
        .update({ primary_color: null, accent_color: null, font_family: null })
        .eq('id', currentOrg.id);

      if (error) throw error;
      setPrimaryColor(DEFAULT_PRIMARY);
      setAccentColor(DEFAULT_ACCENT);
      setFontFamily('__default__');
      setCustomFont('');
      toast.success('Colors reset to defaults');
      window.location.reload();
    } catch (err) {
      toast.error('Reset failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setSaving(false);
    }
  };

  if (tenantLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex-1 space-y-4 px-6 py-6">

      <div>
        <h1 className="text-2xl font-bold tracking-tight">Branding</h1>
        <p className="text-muted-foreground">
          Customize your organization's logo and brand colors
        </p>
      </div>

      <div className="grid gap-6">
        {/* Logo Upload */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              <CardTitle>Logo</CardTitle>
            </div>
            <CardDescription>
              Upload your organization's logo. PNG, SVG, or JPG — max 2MB.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Logo preview on light and dark backgrounds */}
            <div className="flex gap-4">
              <div className="flex items-center justify-center w-24 h-24 rounded-lg border bg-white">
                <img
                  src={logoUrl || '/logo.png'}
                  alt="Logo preview (light)"
                  className="h-16 w-16 object-contain"
                />
              </div>
              <div className="flex items-center justify-center w-24 h-24 rounded-lg border bg-slate-900">
                <img
                  src={logoUrl || '/logo.png'}
                  alt="Logo preview (dark)"
                  className="h-16 w-16 object-contain"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".png,.svg,.jpg,.jpeg"
                onChange={handleLogoUpload}
                className="hidden"
                id="logo-upload"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                Upload Logo
              </Button>
              {logoUrl && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRemoveLogo}
                  disabled={saving}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Remove
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Typography */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Type className="h-5 w-5" />
              <CardTitle>Typography</CardTitle>
            </div>
            <CardDescription>
              Choose a font family that matches your brand identity
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="font-family">Font Family</Label>
              <Select value={fontFamily} onValueChange={setFontFamily}>
                <SelectTrigger id="font-family" className="w-64">
                  <SelectValue placeholder="Select a font" />
                </SelectTrigger>
                <SelectContent>
                  {FONT_OPTIONS.map((font) => (
                    <SelectItem key={font.value} value={font.value}>
                      {font.label}
                    </SelectItem>
                  ))}
                  <SelectItem value="__custom__">Custom Google Font...</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {fontFamily === '__custom__' && (
              <div className="space-y-2">
                <Label htmlFor="custom-font">Google Font Name</Label>
                <Input
                  id="custom-font"
                  value={customFont}
                  onChange={(e) => setCustomFont(e.target.value)}
                  placeholder="e.g. Freight Sans, Roboto Slab"
                  className="w-64"
                />
                <p className="text-xs text-muted-foreground">
                  Enter the exact name from <a href="https://fonts.google.com" target="_blank" rel="noopener noreferrer" className="underline">Google Fonts</a>
                </p>
              </div>
            )}
            <div className="rounded-lg border p-4 bg-muted/30 space-y-1">
              <p className="text-sm font-medium" style={{ fontFamily: fontFamily === '__default__' ? undefined : (fontFamily === '__custom__' ? customFont : fontFamily) }}>
                The quick brown fox jumps over the lazy dog
              </p>
              <p className="text-xs text-muted-foreground" style={{ fontFamily: fontFamily === '__default__' ? undefined : (fontFamily === '__custom__' ? customFont : fontFamily) }}>
                ABCDEFGHIJKLMNOPQRSTUVWXYZ abcdefghijklmnopqrstuvwxyz 0123456789
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Color Customization */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Palette className="h-5 w-5" />
              <CardTitle>Brand Colors</CardTitle>
            </div>
            <CardDescription>
              Set primary and accent colors to match your brand identity
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              {/* Primary color */}
              <div className="space-y-2">
                <Label htmlFor="primary-color">Primary Color</Label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    id="primary-color"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    className="h-10 w-14 cursor-pointer rounded border"
                  />
                  <Input
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    placeholder="#186DE9"
                    className="font-mono w-28"
                  />
                </div>
                {showPrimaryWarning && (
                  <div className="flex items-center gap-1 text-xs text-amber-600">
                    <AlertTriangle className="h-3 w-3" />
                    Low contrast with white text. Consider a darker shade.
                  </div>
                )}
              </div>

              {/* Accent color */}
              <div className="space-y-2">
                <Label htmlFor="accent-color">Accent Color</Label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    id="accent-color"
                    value={accentColor}
                    onChange={(e) => setAccentColor(e.target.value)}
                    className="h-10 w-14 cursor-pointer rounded border"
                  />
                  <Input
                    value={accentColor}
                    onChange={(e) => setAccentColor(e.target.value)}
                    placeholder="#2C9C7F"
                    className="font-mono w-28"
                  />
                </div>
                {showAccentWarning && (
                  <div className="flex items-center gap-1 text-xs text-amber-600">
                    <AlertTriangle className="h-3 w-3" />
                    Low contrast with white text. Consider a darker shade.
                  </div>
                )}
              </div>
            </div>

            {/* Live preview strip */}
            <div className="space-y-2">
              <Label>Preview</Label>
              <div className="flex items-center gap-3 p-4 rounded-lg border bg-muted/30">
                <button
                  className="px-4 py-2 rounded-md text-white text-sm font-medium"
                  style={{ backgroundColor: primaryColor }}
                  type="button"
                >
                  Primary Button
                </button>
                <button
                  className="px-4 py-2 rounded-md text-white text-sm font-medium"
                  style={{ backgroundColor: accentColor }}
                  type="button"
                >
                  Accent Button
                </button>
                <span
                  className="px-2 py-1 rounded text-white text-xs font-medium"
                  style={{ backgroundColor: primaryColor }}
                >
                  Badge
                </span>
                <div
                  className="h-8 w-8 rounded"
                  style={{ backgroundColor: primaryColor }}
                  title="Sidebar swatch"
                />
              </div>
            </div>

            {/* Chart palette preview */}
            <div className="space-y-2">
              <Label>Chart Palette</Label>
              <div className="flex gap-1">
                {chartPalette.slice(0, 5).map((color, i) => (
                  <div
                    key={i}
                    className="h-8 flex-1 rounded-sm first:rounded-l-md last:rounded-r-md"
                    style={{ backgroundColor: color }}
                    title={`Chart color ${i + 1}`}
                  />
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                5 primary chart colors auto-generated from your brand. 5 more are derived for extended palettes.
              </p>
            </div>

            {/* Sidebar header preview */}
            <div className="space-y-2">
              <Label>Sidebar Preview</Label>
              <div className="w-64 rounded-lg border overflow-hidden">
                <div className="h-14 px-4 flex items-center gap-3 bg-[hsl(215,25%,97%)] border-b">
                  <img
                    src={logoUrl || '/logo.png'}
                    alt="Logo"
                    className="h-8 w-8 rounded-lg shadow-md object-contain"
                  />
                  <div>
                    <div className="text-sm font-semibold" style={{ color: 'hsl(215, 25%, 15%)' }}>
                      {currentOrg?.name || 'Your Org'}
                    </div>
                    <div className="text-xs" style={{ color: 'hsl(215, 25%, 15%, 0.6)' }}>
                      Family Office
                    </div>
                  </div>
                </div>
                <div className="p-2 bg-[hsl(215,25%,97%)]">
                  <div
                    className="px-3 py-1.5 rounded text-white text-xs"
                    style={{ backgroundColor: primaryColor }}
                  >
                    Active Nav Item
                  </div>
                  <div className="px-3 py-1.5 text-xs text-muted-foreground">
                    Inactive Item
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={handleResetDefaults} disabled={saving}>
            Reset to Defaults
          </Button>
          <Button onClick={handleSaveColors} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
}
